import { Injectable } from '@angular/core';
import { v4 as uuidV4 } from 'uuid';
import { L10nTranslationService } from 'angular-l10n';
import { Cell, Row, Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { forkJoin, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { FilterView, ToolbarFilterOption } from '../../../filters-toolbar/filters-toolbar.model';
import { FiltersService } from '../../../filters-toolbar/filters.service';
import { FloorClass } from '../../../models/floor.class';
import { AbstractSpaceClass } from '../../../models/space/abstract-space.class';
import { AvailabilitySpaceClass } from '../../../models/space/availability-space.class';
import { NonRentableSpaceClass } from '../../../models/space/non-rentable-space.class';
import { TenantSpaceClass } from '../../../models/space/tenant-space.class';
import { UnaccountedSpaceClass } from '../../../models/space/unaccounted-space.class';
import { MY_FORMATS } from '../../../shared/constants/date.const';
import {
  getTranslationInput,
  processRateOpts,
  sanitizeValueOr,
  stringifyPostalCodeAddress,
} from '../../../shared/utils/functions';
import { MappedLov, PropertyData } from '../../../stacking-plan.model';
import { StackingPlanService } from '../../../stacking-plan.service';
import { ColorSchemeClass } from '../../../theme.const';
import { ElevatorBankProcessed, ElevatorTypes } from '../../floor-container/elevator-banks/elevator-banks.model';
import { FloorsElevators } from '../../stacking-plan.model';
import { Settings, SettingsFilterView, SpaceCardDetails } from '../settings/settings.model';
import { SettingsService } from '../settings/settings.service';
import { BGColors, Colors, COLORS_BY_SCHEME, ColumnKeys } from './export-to-excel.const';
import { ExportSettings, ExportTemplate } from './export.model';

type FloorWithElevatorData = FloorClass & { elevatorData?: ElevatorBankProcessed[] };

@Injectable()
/**
 * @deprecated
 */
export class ExportToExcelService {
  private currentSettings: Settings;
  private filterOptions: { [templateKey: string]: ToolbarFilterOption[] } = {};
  private spaceDetailsOptions: SpaceCardDetails;
  private showElevators: boolean;
  private mappedUomLov: MappedLov;

  constructor(
    private filtersService: FiltersService,
    private stackingPlanService: StackingPlanService,
    private settingsService: SettingsService,
    private translationService: L10nTranslationService
  ) {}

  // TODO: remember to add polyfills to MIQ
  generateWorkbook(
    floorElevatorData: FloorsElevators,
    selectedExportSettings: ExportSettings,
    localeId: number,
    mappedUomLov?: { [uomKey: string]: string }
  ): void {
    this.mappedUomLov = mappedUomLov;
    this.currentSettings = this.settingsService.getSettings();
    this.currentSettings.cardDetails['leaseRateOpts'] = processRateOpts(
      this.currentSettings.cardDetails['leaseRateOpts']
    );
    this.currentSettings.colorScheme.none = ColorSchemeClass.Schema1;
    const workbook: Workbook = new Workbook();

    const { exportTemplate, cardDetails, displayOptions } = selectedExportSettings;

    this.spaceDetailsOptions = cardDetails;
    this.showElevators = displayOptions.showElevators;

    this.getSelectedTemplatesFilterOptions(exportTemplate, localeId).subscribe(templateAndFilters => {
      templateAndFilters.forEach(([templateKey, filters]: [string, ToolbarFilterOption[]]) => {
        this.filterOptions[templateKey] = filters.map(filter => {
          const key: string = this.translationsForKeys(filter, 'key', templateKey);
          const label: string = this.translationsForKeys(filter, 'label');
          if (filter.spaceAreaUOM) {
            const value = mappedUomLov[filter.spaceAreaUOM];
            const spaceAreaUOM = value || null;
            return { ...filter, key, spaceAreaUOM, label };
          } else {
            return { ...filter, key, label };
          }
        });
        this.generateTemplateWorksheet(workbook, floorElevatorData, templateKey as SettingsFilterView);
      });
      // Security: use uuidv4 as entropy source for the filename suffix.
      const uuidHex = uuidV4().replace(/-/g, '').slice(0, 8);
      const filename = `SP_${moment().format('MMDDYYYY')}_${(parseInt(uuidHex, 16) >>> 0) % 1000001}`.toLowerCase();
      this.downloadWorkbook(workbook, `${filename}.xlsx`);
    });
  }

  /**
   *
   * @param filter is @ToolbarFilterOption
   * @param translationLabel is label that needs translation.
   */
  private translationsForKeys(filter: ToolbarFilterOption, translationLabel: string, templateKey?: string) {
    let label: string = filter[translationLabel];
    if (label != null && this.translationService.has(label)) {
      let inputValue = null;
      if (templateKey === FilterView.LeaseYear) {
        inputValue = { year: getTranslationInput(label) };
      }
      return this.translationService.translate(label, inputValue);
    }
    return label;
  }

  /**
   * Depending on the user export template selections, request for each selected
   * template filter options. Map these options in an entry structure, using the
   * original selected template as key.
   *
   * @return tuple of template key and filter options
   * @example
   * ```
   * [
   *   "expiry",
   *   [
   *     {
   *       "id": 1,
   *       ...
   *       "spaces": [...]
   *     }
   *   ]
   * ]
   * ```
   */
  private getSelectedTemplatesFilterOptions(
    exportTemplate: ExportTemplate,
    localeId: number
  ): Observable<(string | ToolbarFilterOption[])[][]> {
    return forkJoin(
      Object.entries(exportTemplate)
        .filter(([_, isSelected]) => isSelected)
        .map(([templateKey]) =>
          this.stackingPlanService
            .getFilterData(
              (templateKey === SettingsFilterView.Expiry ? FilterView.Lease : templateKey) as FilterView,
              localeId,
              true
            )
            .pipe(map(filterOptions => [templateKey, filterOptions]))
        )
    ).pipe(take(1));
  }

  /**
   * Adds a worksheet to the created workbook using the template key as sheet
   * name
   */
  private generateTemplateWorksheet(
    workbook: Workbook,
    floorElevatorData: FloorsElevators,
    template: SettingsFilterView
  ) {
    const sheetTitle = this.translationService.translate(`exportAndSettings.${template}`);
    const worksheet: Worksheet = workbook.addWorksheet(sheetTitle, {
      properties: { defaultColWidth: 22 },
    });
    /*
     * NOTE: custom worksheet attribute to store current template view this
     *  change got applied as the sheet name was being used as key to get data
     *  from different maps. As per this change, the sheet title uses a
     *  translated value, meaning that it was not able to be used as key anymore
     *  Current Usages:
     *   - styleTenantSpaceCell
     *   - generateFiltersLabels
     */
    worksheet.templateKey = template;
    const { floorInfo, elevatorInfoProcessed } = floorElevatorData;
    const floorsWithElevators: FloorWithElevatorData[] = this.mapFloorsAndElevatorInfo(
      floorInfo.filter(floor => !floor.hideFloor),
      elevatorInfoProcessed
    );
    const numberOfElevatorBanks =
      this.showElevators && elevatorInfoProcessed != null && !!elevatorInfoProcessed.length
        ? elevatorInfoProcessed[0].length
        : 0;

    this.defineColumnHeaders(worksheet, this.stackingPlanService.propertyData, numberOfElevatorBanks);
    floorsWithElevators.forEach(this.generateFloorRow.bind(this, worksheet));
    this.generateFillerCell(worksheet);
    this.styleHeaderRow(worksheet);
    this.generateFiltersLabels(worksheet, template);
  }

  private generateFloorRow(worksheet: Worksheet, floor: FloorWithElevatorData, index: number) {
    const floorRow = worksheet.getRow(index + 2);
    const floorNumberCell = floorRow.getCell(ColumnKeys.FloorNumber);
    const floorSpaceCell = floorRow.getCell(ColumnKeys.FloorSpace);
    floorNumberCell.value = floor.toString();
    floorSpaceCell.value = floor.generateFloorSpace(this.mappedUomLov) || '';

    this.styleFloorRow(floorRow);
    this.styleFloorColumn(floorNumberCell);
    this.styleFloorColumn(floorSpaceCell, false);

    this.generateSpacesCells(floorRow, floor.spaces, floor.floorNumber);
    this.generateElevatorBankCells(floorRow, floor.elevatorData);
  }

  private downloadWorkbook(workbook: Workbook, filename: string): void {
    workbook.xlsx.writeBuffer().then(data => {
      let blob = new Blob([data]);
      saveAs(blob, filename);
    });
  }

  private generateSpacesCells(floorRow: Row, spaces: AbstractSpaceClass[], floorNumber: string) {
    if (spaces != null && !!spaces.length) {
      let nextCell = floorRow.cellCount + 1;
      spaces.forEach(space => {
        if (!(space instanceof UnaccountedSpaceClass)) {
          nextCell = this.generateSpaceCell(floorRow, nextCell, space, floorNumber);
        }
      });
    }
  }

  private defineColumnHeaders(worksheet, propertyData: PropertyData, numberOfElevatorBanks: number) {
    const elevatorColumns = Array(numberOfElevatorBanks).fill({ width: 2 });
    worksheet.columns = [
      ...elevatorColumns,
      {
        header: this.translationService.translate('exportToExcel.floorNumberHeader'),
        key: ColumnKeys.FloorNumber,
        width: 18,
      },
      {
        header: this.translationService.translate('exportToExcel.floorSizeHeader'),
        key: ColumnKeys.FloorSpace,
        width: 20,
      },
      {
        header: `${this.generateReportTitle(propertyData)}`,
        key: ColumnKeys.Spaces,
        width: 24,
      },
    ];
  }

  private generateSpaceCell(
    floorRow: Row,
    currentCellPosition: number,
    space: AbstractSpaceClass,
    floorNumber: string
  ): number {
    // NOTE: round the space area to the nearest thousand values. If result is 0,
    // default must be 1
    const cellsToOccupy = Math.round((space.getAreaSpace() || 0) / 1000) || 1;
    const firstCell: Cell = floorRow.getCell(currentCellPosition);
    let lastCellToMerge = firstCell;
    if (cellsToOccupy > 1) {
      const worksheet = floorRow.worksheet;
      lastCellToMerge = floorRow.getCell(currentCellPosition + cellsToOccupy - 1);
      worksheet.mergeCells(`${firstCell.address}:${lastCellToMerge.address}`);
    }

    firstCell.value = space
      .toString({ detailsToDisplay: this.spaceDetailsOptions, mappedUomLov: this.mappedUomLov })
      .trim();
    this.styleSpaceCell(firstCell, space, floorNumber);
    return lastCellToMerge.col + 1;
  }

  /**
   * A filler cell is the used to fill the rest of the floors, using the max
   * column count from the worksheet. The filler cell is the merge of the first
   * cell after the last existing space cell and the last column of the row with
   * more cells.
   *
   * For the first row, the header row, it uses the existing 'spaces' header as
   * the cell to begin the merge.
   */
  private generateFillerCell(worksheet: Worksheet) {
    const maxNumberOfColumns = worksheet.columnCount;
    worksheet.eachRow((row: Row) => {
      const isHeaderRow = row.number === 1;
      const rowNumberOfCells = row.cellCount;
      const lastFillerCell = row.getCell(maxNumberOfColumns);
      // do not fill first row (header)
      const firstCellNumber = !isHeaderRow ? rowNumberOfCells + 1 : rowNumberOfCells;
      const firstFillerCell = row.getCell(firstCellNumber);
      if (rowNumberOfCells !== lastFillerCell.col) {
        worksheet.mergeCells(`${firstFillerCell.address}:${lastFillerCell.address}`);
        firstFillerCell.value = isHeaderRow ? firstFillerCell.value : '';
        this.styleSpaceCell(firstFillerCell);
      }
    });
  }

  private mapFloorsAndElevatorInfo(
    floors: FloorClass[],
    elevatorBanks: ElevatorBankProcessed[][]
  ): FloorWithElevatorData[] {
    return this.showElevators && elevatorBanks != null && !!elevatorBanks.length
      ? floors.map<FloorWithElevatorData>((floor, index) => {
          if (floor.floorAreaUOM) {
            const value = this.mappedUomLov[floor.floorAreaUOM];
            floor.floorAreaUOM = value || floor.floorAreaUOM;
          }
          const clonedFloor = floor.clone() as FloorWithElevatorData;
          clonedFloor.elevatorData = elevatorBanks[index];
          return clonedFloor;
        })
      : floors;
  }

  private styleFloorRow(floorRow: Row) {
    floorRow.height = Object.values(this.spaceDetailsOptions).some(isFieldRequired => isFieldRequired)
      ? (floorRow.height * 20) / floorRow.width
      : 40;
    floorRow.border = { bottom: { style: 'thin', color: { argb: Colors.White } } };
    floorRow.alignment = { vertical: 'middle', wrapText: true };
    floorRow.font = { name: 'Arial' };
  }

  private styleSpaceCell(spaceCell: Cell, space?: AbstractSpaceClass, floorNumber?: string) {
    spaceCell.alignment = { wrapText: true, indent: 1, vertical: 'middle' };
    spaceCell.border = {
      right: { style: 'thin', color: { argb: Colors.White } },
      bottom: { style: 'thin', color: { argb: Colors.White } },
    };
    if ((space != null && space instanceof AvailabilitySpaceClass) || space instanceof NonRentableSpaceClass) {
      spaceCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BGColors.DefaultBG } };
    } else if (space !== null && space instanceof TenantSpaceClass) {
      this.styleTenantSpaceCell(spaceCell, space, floorNumber);
    } else {
      spaceCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BGColors.DefaultBG } };
    }
  }

  /**
   * depending on the selected template (stored and used as the worksheet name)
   * and the current user settings theme, color to the tenant space cell will
   * be applied
   *
   * Notice that a custom filter options array is passed to
   * {@link getItemSchemaClass} so the service uses the template filter values
   * instead of the current view filters.
   */
  private styleTenantSpaceCell(spaceCell: Cell, space: TenantSpaceClass, floorNumber: string) {
    const exportTemplate: SettingsFilterView = spaceCell.worksheet.templateKey;
    const { class: klass = '' } = this.filtersService.getItemSchemaClass(
      space,
      floorNumber,
      this.filterOptions[exportTemplate]
    );
    const viewColorScheme = this.currentSettings.colorScheme[exportTemplate];
    const colorPalette = COLORS_BY_SCHEME[viewColorScheme || 'Default'][klass];
    const fontColor = klass === 'expire-na' ? Colors.DefaultText : Colors.White;
    spaceCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colorPalette } };
    spaceCell.font = { ...(spaceCell.font != null ? spaceCell.font : {}), color: { argb: fontColor } };
  }

  private styleFloorColumn(floorCell: Cell, hasRightBorder = true) {
    floorCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BGColors.FloorColumn } };
    floorCell.border = { bottom: { style: 'thin', color: { argb: Colors.White } } };
    if (hasRightBorder) {
      floorCell.border.right = { style: 'thin', color: { argb: Colors.White } };
    }
    floorCell.alignment = { horizontal: 'center', vertical: 'middle' };
    floorCell.font = {
      ...(floorCell.font != null ? floorCell.font : {}),
      bold: true,
      color: { argb: Colors.DefaultText },
    };
  }

  private styleHeaderRow(worksheet: Worksheet) {
    const headerRow: Row = worksheet.getRow(1);
    headerRow.height = 40;
    headerRow.border = null;
    headerRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    headerRow.font = {
      name: 'Arial',
      size: 14,
      bold: true,
      color: { argb: Colors.White },
    };
    const floorDataColumnFillConfig = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: BGColors.FloorColumnHeader },
    };
    const floorNumberCell = headerRow.getCell(ColumnKeys.FloorNumber);
    const firstHeaderCell = headerRow.getCell(1);
    if (floorNumberCell !== firstHeaderCell) {
      /*
       * if firstHeaderCell is different to floorNumberCell, it means that no
       * elevator banks exist. Therefore, merge and format Elevator Bank header
       * cells.
       */
      worksheet.mergeCells(`${firstHeaderCell.address}:${headerRow.getCell(floorNumberCell.col - 1).address}`);
      firstHeaderCell.border = { bottom: { style: 'thin', color: { argb: Colors.White } } };
    }
    headerRow.getCell(ColumnKeys.FloorSpace).fill = floorDataColumnFillConfig;

    floorNumberCell.fill = floorDataColumnFillConfig;
    const spaceHeader = headerRow.getCell(headerRow.cellCount);
    spaceHeader.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: BGColors.SpacesHeader },
    };
    spaceHeader.alignment = { vertical: 'middle', indent: 1, wrapText: true };
  }

  private generateFiltersLabels(worksheet: Worksheet, template: SettingsFilterView) {
    let firstFiltersRowNumber = worksheet.lastRow.number + 2;
    const viewColorScheme = this.currentSettings.colorScheme[template];
    const currentSheetColorScheme = COLORS_BY_SCHEME[viewColorScheme || 'Default'];
    const filterLabelsAndClasses = this.filterOptions[worksheet.templateKey].map(({ class: klass, label, key }) => [
      currentSheetColorScheme[klass],
      label || key,
    ]);
    // use first row/cell to display the filters template name
    const filterHeaderRow: Row = worksheet.getRow(firstFiltersRowNumber++);
    const templateHeaderCell = filterHeaderRow.getCell(ColumnKeys.FloorNumber);
    templateHeaderCell.value = worksheet.name.toUpperCase();
    filterHeaderRow.height = 22;
    filterHeaderRow.alignment = { vertical: 'middle', indent: 1 };

    filterLabelsAndClasses.forEach(([colorArgb, label], index, arr) => {
      const filterRow: Row = worksheet.getRow(firstFiltersRowNumber++);
      const colorCell = filterRow.getCell(ColumnKeys.FloorNumber);
      const labelCell = filterRow.getCell(ColumnKeys.FloorSpace);

      // Rows and Cells values
      colorCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colorArgb } };
      labelCell.value = label;

      // Rows and Cells styles
      filterRow.height = 22;
      filterRow.alignment = { vertical: 'middle', indent: 1 };
      const borderStyle = { style: 'thin', color: { argb: Colors.DefaultText } };
      colorCell.border = {};
      labelCell.border = {};
      if (index === 0) {
        colorCell.border.top = borderStyle;
        labelCell.border.top = borderStyle;
      }
      if (index >= 0) {
        colorCell.border.left = borderStyle;
        labelCell.border.right = borderStyle;
      }
      if (index === arr.length - 1) {
        colorCell.border.bottom = borderStyle;
        labelCell.border.bottom = borderStyle;
      }
    });
  }

  private generateElevatorBankCells(floorRow: Row, elevatorData: ElevatorBankProcessed[]) {
    (elevatorData || []).forEach((elevator, index) => {
      const elevatorCell = floorRow.getCell(index + 1);
      elevatorCell.border = {
        bottom: { style: 'thin', color: { argb: Colors.White } },
        right: { style: 'thin', color: { argb: Colors.White } },
      };
      if (elevator.elevatorType !== ElevatorTypes.ThinLine && elevator.elevatorType !== ElevatorTypes.Empty) {
        elevatorCell.border.bottom = {};
        elevatorCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BGColors.ElevatorBank } };
      }
    });
  }

  private generateReportTitle(propertyData: PropertyData): string {
    const propertyName = sanitizeValueOr('-', propertyData.propertyName);
    const propertyAddress = stringifyPostalCodeAddress(propertyData.postalAddress);
    return `${propertyName} - ${propertyAddress}\n${moment().format(MY_FORMATS)}`;
  }
}
